'use client';

import { useEffect, useRef } from 'react';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { hasBookableTourProvider } from '@/src/lib/utils/instantTourProviders';
import { BOOK_TOUR_HASH } from '@/src/lib/constants';
import { useHostedTourAction } from './TourActionHost';

/**
 * Opens the tour wizard when the page loads with the #book-tour hash — the
 * deep link published as `tourBookingPage` in the listing structured data.
 * One-shot: closing the wizard doesn't re-open it while the hash remains.
 *
 * Opens the page's shared wizard rather than one of its own — see `TourActionHost`.
 */
export function BookTourHashOpener({ listing }: { listing?: PropertyDetailListing | null }) {
  const { openTour } = useHostedTourAction('book_tour_hash', listing);
  const opened = useRef(false);
  const bookable = hasBookableTourProvider(listing?.details?.instantTourProviders);

  useEffect(() => {
    if (opened.current || !bookable || window.location.hash !== `#${BOOK_TOUR_HASH}`) {
      return;
    }
    opened.current = true;
    openTour();
  }, [bookable, openTour]);

  return null;
}
